// ds-color-ignore-file: dev-only / internal tooling surface
"use client"

import { useEffect, useRef, useState } from "react"
import type { MessageIndex } from "@/scripts/build-message-index"
import type { Provenance, CmsProvenance } from "./provenance"
import type { ExperimentProvenance } from "./experiment-provenance"
import { cmsCartKey, type CartItem } from "./edit-cart"
import { ExperimentBadge } from "./locale-grid-modal"
import { isLargeString } from "../review/locate-state"
import {
  useStringStatus,
  GateNote,
  ApprovalsChip,
  ApprovalMark,
} from "./string-status-view"

/**
 * In-context string editor that lives in the cockpit's Edit tab (AHD-7487),
 * not a page-covering modal — so the on-page render stays visible on the left
 * while you edit on the right. As you type the focus-locale value it live-writes
 * to the picked DOM node (`onLiveChange`).
 *
 * Two escape hatches keep it honest:
 *  - **Huge strings** (legal copy, paragraph breaks) don't fit a rail textarea,
 *    so they show a preview + "Edit in full editor" button that opens the modal
 *    (`onOpenFull`) — same pattern as the translation-review panel.
 *  - The **Lexical rich-text body** editor (AHD-7412) is modal-only and never
 *    routes here.
 */
export function EditStringPanel({
  provenance,
  cmsProvenance,
  index,
  page,
  experiment,
  existing,
  cmsSeedText,
  cmsOriginalOld,
  focusLocale,
  idToken,
  autoFocus,
  externalFocusValue,
  fullOpen,
  onRegisterCommit,
  onLiveChange,
  onAddToCart,
  onOpenFull,
  onClose,
}: {
  provenance: Provenance | null
  cmsProvenance: CmsProvenance | null
  index: MessageIndex | null
  page: string
  experiment?: ExperimentProvenance
  existing?: CartItem
  cmsSeedText: string
  cmsOriginalOld?: string
  focusLocale: string
  /** SSO/dev idToken — sent as User-Authorization to the review status API.
   *  Optional: status (gate/approval) is only fetched when a token is present. */
  idToken?: string
  /** Autofocus the focus-locale textarea on mount (default true). False when the
   *  pick is edited in place, so the contentEditable page node keeps the caret. */
  autoFocus?: boolean
  externalFocusValue?: string | null
  /** The full-screen editor is open for this pick — collapse the rail to a hint
   *  so the same field isn't editable in two places at once. (finding 2) */
  fullOpen?: boolean
  onRegisterCommit?: (fn: () => void) => void
  onLiveChange: (value: string) => void
  onAddToCart: (item: CartItem) => void
  onOpenFull: () => void
  onClose: (committed?: boolean) => void
}) {
  // The full editor owns the field while it's open — don't render a second,
  // competing editor surface in the rail. (finding 2)
  if (fullOpen) return <FullEditorHint onClose={onClose} />
  if (cmsProvenance) {
    return (
      <CmsPanel
        provenance={cmsProvenance}
        page={page}
        seedText={cmsSeedText}
        originalOld={cmsOriginalOld}
        experiment={experiment}
        autoFocus={autoFocus}
        externalFocusValue={externalFocusValue}
        onRegisterCommit={onRegisterCommit}
        onLiveChange={onLiveChange}
        onAddToCart={onAddToCart}
        onOpenFull={onOpenFull}
        onClose={onClose}
      />
    )
  }
  if (provenance && index) {
    return (
      <IntlPanel
        provenance={provenance}
        index={index}
        page={page}
        experiment={experiment}
        existing={existing}
        focusLocale={focusLocale}
        idToken={idToken}
        autoFocus={autoFocus}
        externalFocusValue={externalFocusValue}
        onRegisterCommit={onRegisterCommit}
        onLiveChange={onLiveChange}
        onAddToCart={onAddToCart}
        onOpenFull={onOpenFull}
        onClose={onClose}
      />
    )
  }
  return null
}

function IntlPanel({
  provenance,
  index,
  page,
  experiment,
  existing,
  focusLocale,
  idToken,
  autoFocus,
  externalFocusValue,
  onRegisterCommit,
  onLiveChange,
  onAddToCart,
  onOpenFull,
  onClose,
}: {
  provenance: Provenance
  index: MessageIndex
  page: string
  experiment?: ExperimentProvenance
  existing?: CartItem
  focusLocale: string
  idToken?: string
  autoFocus?: boolean
  externalFocusValue?: string | null
  onRegisterCommit?: (fn: () => void) => void
  onLiveChange: (value: string) => void
  onAddToCart: (item: CartItem) => void
  onOpenFull: () => void
  onClose: (committed?: boolean) => void
}) {
  const [key, setKey] = useState<string | null>(
    provenance.status === "exact" ? provenance.key : null
  )
  const [draft, setDraft] = useState<Record<string, string>>({})
  const focusRef = useRef<HTMLTextAreaElement | null>(null)
  const { status } = useStringStatus(key, idToken)
  const approvalByLocale = new Map(
    (status?.approvals ?? []).map((a) => [a.locale, a])
  )

  const locales = key ? (index.byKey[key] ?? {}) : {}
  // Locales the reviewer has actually changed vs the source — drives the dirty
  // highlight and the count on the Add button. Computed (and `commit` +
  // `useSaveShortcut` below) BEFORE the early returns so the hook order is
  // constant: an ambiguous pick flips `key` null→set, and a hook that only ran
  // after that transition would crash with "rendered more hooks than expected".
  const changedLocales = key
    ? Object.keys(locales).filter(
        (loc) => (draft[loc] ?? "") !== (locales[loc] ?? "")
      )
    : []
  const n = changedLocales.length

  useEffect(() => {
    if (!key) return
    const staged =
      existing && existing.key === key
        ? Object.fromEntries(
            Object.entries(existing.values).map(([l, v]) => [l, v.new])
          )
        : {}
    setDraft({ ...(index.byKey[key] ?? {}), ...staged })
  }, [key, index, existing])

  useEffect(() => {
    if (autoFocus === false) return
    const t = focusRef.current
    if (!t) return
    t.focus()
    t.setSelectionRange(t.value.length, t.value.length)
  }, [key, autoFocus])

  const commit = () => {
    if (!key || !n) return
    const values: CartItem["values"] = {}
    for (const loc of changedLocales)
      values[loc] = {
        old: existing?.values[loc]?.old ?? locales[loc],
        new: draft[loc],
      }
    onAddToCart({ key, source: "intl", page, values, experiment })
    onClose(true)
  }
  useSaveShortcut(commit, n > 0)

  // DOM→panel: adopt a value typed directly into the page node, into the focus
  // locale (the one rendered on this page).
  useEffect(() => {
    if (!key || externalFocusValue == null) return
    setDraft((d) =>
      (d[focusLocale] ?? "") === externalFocusValue
        ? d
        : { ...d, [focusLocale]: externalFocusValue }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalFocusValue, key])

  useEffect(() => {
    onRegisterCommit?.(commit)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, draft])

  if (provenance.status === "miss") {
    return (
      <Frame title="Couldn’t locate this string" onClose={onClose}>
        <p style={{ fontSize: 12.5, color: "#555", lineHeight: 1.4 }}>
          No index match (dynamic or concatenated text). Key search is a later
          phase.
        </p>
      </Frame>
    )
  }

  if (!key && provenance.status === "ambiguous") {
    return (
      <Frame title="Which key produced this?" onClose={onClose}>
        <p style={{ fontSize: 11.5, color: "#888", margin: "0 0 8px" }}>
          ambiguous — confirm before editing
        </p>
        {provenance.candidates.map((k, i) => (
          <label key={k} style={ambiguousRow}>
            <input
              type="radio"
              name="ahd-panel-dk"
              defaultChecked={i === 0}
              onChange={() => setKey(k)}
            />
            <code style={{ fontSize: 11.5 }}>{k}</code>
            {i === 0 ? (
              <span
                style={{ marginLeft: "auto", fontSize: 10, color: "#2b6cff" }}
              >
                on this page
              </span>
            ) : null}
          </label>
        ))}
        <button
          style={primaryBtn}
          onClick={() => setKey(provenance.candidates[0])}
        >
          Continue
        </button>
      </Frame>
    )
  }

  const k = key!
  const isTemplate = index.flags[k] === "template"
  // "Huge" is judged on what renders on this page (the focus-locale source), so
  // long legal copy routes to the full modal instead of a cramped rail textarea.
  const onPageValue = index.byKey[k]?.[focusLocale] ?? ""
  const large = isLargeString(onPageValue)

  return (
    <Frame title={k} onClose={onClose}>
      <GateNote tier={status?.tier ?? null} />
      <ApprovalsChip
        approvals={status?.approvals ?? []}
        focusLocale={focusLocale}
      />
      <ExperimentBadge experiment={experiment} />
      {isTemplate ? (
        <p style={{ color: "#b25e00", fontSize: 11.5, margin: "0 0 8px" }}>
          Contains a {"{placeholder}"} — editing the raw template.
        </p>
      ) : null}

      {large ? (
        <LargeTeaser value={onPageValue} onOpenFull={onOpenFull} />
      ) : (
        Object.keys(locales).map((loc) => {
          const isFocusLoc = focusLocale === loc
          const dup =
            !isFocusLoc &&
            locales[focusLocale] !== undefined &&
            locales[loc] === locales[focusLocale]
          const changed = changedLocales.includes(loc)
          return (
            <div key={loc} style={{ marginBottom: 9 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                <strong
                  style={{
                    fontSize: 11.5,
                    color: isFocusLoc ? "#2b6cff" : "#666",
                    fontWeight: isFocusLoc ? 700 : 600,
                  }}
                >
                  {loc.toUpperCase()}
                </strong>
                {changed ? (
                  <span style={editedTag}>● edited</span>
                ) : isFocusLoc ? (
                  <span
                    style={{ fontSize: 9, color: "#2b6cff", fontWeight: 700 }}
                  >
                    ● on this page
                  </span>
                ) : dup ? (
                  <span style={{ fontSize: 9, color: "#999" }}>
                    = same as {focusLocale.toUpperCase()}
                  </span>
                ) : null}
              </div>
              {approvalByLocale.has(loc) ? (
                <ApprovalMark approval={approvalByLocale.get(loc)!} />
              ) : null}
              <textarea
                ref={isFocusLoc ? focusRef : undefined}
                aria-label={loc}
                value={draft[loc] ?? ""}
                onChange={(e) => {
                  const v = e.target.value
                  setDraft((d) => ({ ...d, [loc]: v }))
                  if (isFocusLoc) onLiveChange(v)
                }}
                style={{
                  ...fieldBox,
                  ...(changed ? dirtyFieldBox : null),
                  borderColor: changed
                    ? DIRTY
                    : isFocusLoc
                      ? "#2b6cff"
                      : dup
                        ? "#e7c200"
                        : "#dcdce0",
                }}
              />
            </div>
          )
        })
      )}
      {large ? null : (
        <ActionBar>
          <button
            style={n ? primaryBtn : disabledBtn}
            disabled={!n}
            onClick={commit}
          >
            {n ? `Add ${n} change${n > 1 ? "s" : ""} to cart` : "Add to cart"}
            {n ? <kbd style={kbdHint}>⌘S</kbd> : null}
          </button>
        </ActionBar>
      )}
    </Frame>
  )
}

function CmsPanel({
  provenance,
  page,
  seedText,
  originalOld,
  experiment,
  autoFocus,
  externalFocusValue,
  onRegisterCommit,
  onLiveChange,
  onAddToCart,
  onOpenFull,
  onClose,
}: {
  provenance: CmsProvenance
  page: string
  seedText: string
  originalOld?: string
  experiment?: ExperimentProvenance
  autoFocus?: boolean
  externalFocusValue?: string | null
  onRegisterCommit?: (fn: () => void) => void
  onLiveChange: (value: string) => void
  onAddToCart: (item: CartItem) => void
  onOpenFull: () => void
  onClose: (committed?: boolean) => void
}) {
  const { collection, id, field, locale } = provenance
  const cartKey = cmsCartKey({ collection, id, field })
  const [value, setValue] = useState(seedText)
  const base = originalOld ?? seedText
  const large = isLargeString(seedText)
  const changed = value !== base
  const focusRef = useRef<HTMLTextAreaElement | null>(null)
  // The host seeds from the DOM/published text at pick-time, then resolves the
  // field's latest CMS draft async (~100ms later) and bumps `seedText`. Adopt
  // that late seed — but only while pristine, so it never clobbers in-progress
  // typing or flips a still-untouched field to "edited". `touchedRef` keys off a
  // real keystroke, not `value !== base` (a draft ≠ published seed isn't a user
  // edit). The panel remounts per pick via a host `key`, so this only handles
  // the same-pick late-seed case.
  const touchedRef = useRef(false)

  useEffect(() => {
    if (!touchedRef.current) setValue(seedText)
  }, [seedText])

  useEffect(() => {
    if (autoFocus === false) return
    const t = focusRef.current
    if (!t) return
    t.focus()
    t.setSelectionRange(t.value.length, t.value.length)
  }, [autoFocus])

  const commit = () => {
    if (!changed) return
    onAddToCart({
      key: cartKey,
      source: "cms",
      page,
      values: { [locale]: { old: base, new: value } },
      cms: { collection, id, field },
      experiment,
    })
    onClose(true)
  }
  useSaveShortcut(commit, changed)

  // DOM→panel: adopt a value typed directly into the page node.
  useEffect(() => {
    if (externalFocusValue != null && externalFocusValue !== value) {
      touchedRef.current = true
      setValue(externalFocusValue)
    }
    // adopt only on external changes; `value` intentionally omitted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalFocusValue])

  // Let the host trigger this panel's commit (inline Enter).
  useEffect(() => {
    onRegisterCommit?.(commit)
    // commit closes over value/changed; re-register when they change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const isHtml = provenance.kind === "html"

  return (
    <Frame title={cartKey} onClose={onClose}>
      <div style={{ ...cmsBadge }}>Saves as a draft — needs publishing.</div>
      {isHtml ? (
        <div style={htmlHint} title="This field stores raw HTML markup.">
          HTML · this field accepts HTML
        </div>
      ) : null}
      <ExperimentBadge experiment={experiment} />
      {large ? (
        <LargeTeaser value={seedText} onOpenFull={onOpenFull} />
      ) : (
        <>
          <div style={{ marginBottom: 9 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
              <strong
                style={{ fontSize: 11.5, color: "#2b6cff", fontWeight: 700 }}
              >
                {locale.toUpperCase()}
              </strong>
              {changed ? <span style={editedTag}>● edited</span> : null}
            </div>
            <textarea
              ref={focusRef}
              aria-label={locale}
              value={value}
              onChange={(e) => {
                touchedRef.current = true
                setValue(e.target.value)
                onLiveChange(e.target.value)
              }}
              style={{
                ...fieldBox,
                ...(changed ? dirtyFieldBox : null),
                borderColor: changed ? DIRTY : "#2b6cff",
                minHeight: 60,
                ...(isHtml ? { fontFamily: "ui-monospace, monospace" } : null),
              }}
            />
          </div>
          <ActionBar>
            <button
              style={
                changed ? { ...primaryBtn, background: "#2f6f3e" } : disabledBtn
              }
              disabled={!changed}
              onClick={commit}
            >
              {changed ? "Add 1 change to cart" : "Add to cart"}
              {changed ? <kbd style={kbdHint}>⌘S</kbd> : null}
            </button>
          </ActionBar>
        </>
      )}
    </Frame>
  )
}

/** ⌘/Ctrl+S commits the open edit (add to cart) when there's something to stage.
 *  Capture phase + stopPropagation so it beats the browser Save dialog and the
 *  review worklist's single-key handlers. */
function useSaveShortcut(commit: () => void, enabled: boolean) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && (e.key === "s" || e.key === "S")) {
        e.preventDefault()
        e.stopPropagation()
        if (enabled) commit()
      }
    }
    document.addEventListener("keydown", onKey, true)
    return () => document.removeEventListener("keydown", onKey, true)
  }, [commit, enabled])
}

/** Sticky bottom action bar so "Add to cart" stays visible while the textareas
 *  above it scroll (the rail body is the scroll container). */
function ActionBar({ children }: { children: React.ReactNode }) {
  return <div className="ahead-edit-actions">{children}</div>
}

/** Shown in the rail while the full-screen editor owns the current pick, so the
 *  field isn't presented as editable in two places. (finding 2) */
function FullEditorHint({
  onClose,
}: {
  onClose: (committed?: boolean) => void
}) {
  return (
    <Frame title="Editing in the full editor" onClose={onClose}>
      <p style={{ fontSize: 12.5, color: "#555", lineHeight: 1.4, margin: 0 }}>
        This field is open in the full editor. Finish there, then close it to
        edit anything else.
      </p>
    </Frame>
  )
}

/** Preview + open-the-modal button for strings too long for a rail textarea. */
function LargeTeaser({
  value,
  onOpenFull,
}: {
  value: string
  onOpenFull: () => void
}) {
  return (
    <div style={largeWrap}>
      <p style={largePreview}>
        {value.slice(0, 200)}
        {value.length > 200 ? "…" : ""}
      </p>
      <button style={{ ...primaryBtn, marginTop: 0 }} onClick={onOpenFull}>
        Edit in full editor
      </button>
    </div>
  )
}

/** Shared panel chrome: title row + close (×) and a scroll-friendly body. */
function Frame({
  title,
  onClose,
  children,
}: {
  title: string
  onClose: (committed?: boolean) => void
  children: React.ReactNode
}) {
  // Escape closes (matches the modal). Capture phase so it wins over the
  // worklist's single-key handlers.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation()
        onClose()
      }
    }
    document.addEventListener("keydown", onKey, true)
    return () => document.removeEventListener("keydown", onKey, true)
  }, [onClose])

  return (
    <div
      data-ahead-edit-panel
      style={{ fontFamily: "ui-sans-serif, system-ui" }}
    >
      <div style={titleRow}>
        <code style={{ fontSize: 12, wordBreak: "break-all" }}>{title}</code>
        <button
          aria-label="Close editor"
          style={closeBtn}
          onClick={() => onClose()}
        >
          ×
        </button>
      </div>
      {children}
    </div>
  )
}

const titleRow: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: 8,
  marginBottom: 8,
}
const closeBtn: React.CSSProperties = {
  marginLeft: "auto",
  flexShrink: 0,
  border: "none",
  background: "transparent",
  color: "#888",
  fontSize: 18,
  lineHeight: 1,
  cursor: "pointer",
}
// "Dirty" accent for a changed input — amber, matching the preview banner's
// "unsaved / not pushed yet" chrome so a changed box reads as pending.
const DIRTY = "#e0962a"
const editedTag: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 700,
  color: DIRTY,
}
const dirtyFieldBox: React.CSSProperties = {
  background: "#fff8ee",
  boxShadow: "0 0 0 1px #e0962a",
}
const fieldBox: React.CSSProperties = {
  width: "100%",
  marginTop: 3,
  border: "1px solid #dcdce0",
  borderRadius: 8,
  padding: "7px 9px",
  fontSize: 13,
  minHeight: 36,
  resize: "vertical",
  boxSizing: "border-box",
}
const primaryBtn: React.CSSProperties = {
  width: "100%",
  marginTop: 4,
  border: "none",
  borderRadius: 9,
  padding: "9px 16px",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  background: "#0b0b0f",
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
}
const disabledBtn: React.CSSProperties = {
  ...{
    width: "100%",
    marginTop: 4,
    border: "none",
    borderRadius: 9,
    padding: "9px 16px",
    fontSize: 13,
    fontWeight: 600,
  },
  background: "#e7e7ea",
  color: "#9a9aa2",
  cursor: "default",
}
const kbdHint: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  opacity: 0.7,
  border: "1px solid rgba(255,255,255,.5)",
  borderRadius: 4,
  padding: "0 4px",
}
const cmsBadge: React.CSSProperties = {
  display: "inline-block",
  fontSize: 11,
  fontWeight: 700,
  color: "#2f6f3e",
  background: "#e7f6ec",
  borderRadius: 6,
  padding: "3px 8px",
  marginBottom: 10,
}
const htmlHint: React.CSSProperties = {
  display: "inline-block",
  fontSize: 11,
  fontWeight: 700,
  color: "#5a4a22",
  background: "#fdf6e9",
  border: "1px solid #f0d8a8",
  borderRadius: 6,
  padding: "3px 8px",
  marginBottom: 10,
  marginLeft: 6,
  fontFamily: "ui-monospace, monospace",
}
const ambiguousRow: React.CSSProperties = {
  display: "flex",
  gap: 8,
  alignItems: "center",
  padding: "8px 10px",
  border: "1px solid #e4e4e8",
  borderRadius: 9,
  marginBottom: 7,
  cursor: "pointer",
}
const largeWrap: React.CSSProperties = {
  margin: "2px 0 4px",
  padding: 9,
  border: "1px dashed #cbd5cf",
  borderRadius: 8,
  background: "#f6f8f7",
}
const largePreview: React.CSSProperties = {
  margin: "0 0 8px",
  fontSize: 12,
  color: "#555",
  lineHeight: 1.4,
  whiteSpace: "pre-wrap",
  maxHeight: 90,
  overflow: "hidden",
}
